import { ALLOWED_DRAFT_TAGS, DROP_WITH_CONTENT } from "@context/shared";

/**
 * Tags whose *contents* are machinery, not the student's words. A full-page
 * copy pastes `<title>`, `<style>` and `<script>` as real elements; recursing
 * into them would insert the page title, CSS and JS as visible text at the
 * caret. The set is shared with the server sanitizer so the two cannot drift.
 *
 * The form-control tags (textarea, select, option, ...) are included for the
 * same reason: their placeholder/option/label text would otherwise leak into
 * the caret as visible text on a full-page paste.
 */

/**
 * Rebuilds stored draft markup into a tree containing only allowlisted tags and
 * text, with every attribute discarded.
 *
 * The server sanitises on write, but this is the barrier that decides what the
 * browser actually renders, and it is built on the browser's own parser rather
 * than pattern matching — so a tokenizer quirk on the server cannot turn into
 * script execution here. Defence in depth for the one place the app assigns
 * `innerHTML`.
 */
export function sanitizeDraftHtmlForDom(html: string): string {
  if (!html) return "";

  const parsed = new DOMParser().parseFromString(`<div id="root">${html}</div>`, "text/html");
  const source = parsed.getElementById("root");
  if (!source) return "";

  const target = document.createElement("div");
  copyAllowedChildren(source, target);
  return target.innerHTML;
}

function escapeHtmlText(text: string): string {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/**
 * Rebuilds draft markup from the plain structured text of a compact session
 * (saved without its HTML when storage was full). Each blank-line-separated
 * paragraph becomes one `<p>`, and a single newline inside a paragraph becomes
 * a `<br>` so the structure survives the round trip through
 * `htmlToStructuredText`. The text is escaped before it is wrapped, so stored
 * words that look like markup stay literal, and the result goes through the
 * same allowlist sanitizer as every other HTML write. Empty (or whitespace-
 * only) text yields an empty string, keeping the editor's empty state.
 */
export function draftHtmlFromStructuredText(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return "";

  const html = trimmed
    .split(/\n{2,}/)
    .map((paragraph) => `<p>${escapeHtmlText(paragraph).replace(/\n/g, "<br>")}</p>`)
    .join("");

  return sanitizeDraftHtmlForDom(html);
}

function copyAllowedChildren(source: Node, target: Node): void {
  source.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      target.appendChild(document.createTextNode(child.textContent ?? ""));
      return;
    }

    if (child.nodeType !== Node.ELEMENT_NODE) return;

    const element = child as Element;
    const tag = element.tagName.toLowerCase();

    if (!ALLOWED_DRAFT_TAGS.has(tag)) {
      // Head-ish elements carry CSS/JS/title text rather than the student's
      // words: drop the element AND its contents so a full-page copy cannot
      // leak the page title or a stylesheet into the caret.
      if (DROP_WITH_CONTENT.has(tag)) return;
      // Keep the words, drop the element: a disallowed wrapper must not silently
      // delete a student's text.
      copyAllowedChildren(element, target);
      return;
    }

    // Created fresh, so no attribute of any kind survives.
    const clean = document.createElement(tag);
    copyAllowedChildren(element, clean);
    target.appendChild(clean);
  });
}
